import { useEffect, useState, type Dispatch } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "@repo/i18n";
import type { BookingConfig, VenueConfig } from "../../config/types";
import { SlotList, type DisplaySlot } from "../components/SlotList";
import { OverflowCard } from "../components/OverflowCard";
import { formatServiceDate } from "../format";
import {
  bookingRejectionOf,
  fetchAlternatives,
  placeHold,
  useGroupAvailability,
  useOpenSlots,
} from "../publicApi";
import { classifyRejection, rejectionMessageKey } from "../rejection";
import type { FlowAction, FlowState, SelectedSlot } from "../flow/state";

type SlotsState = Extract<FlowState, { step: "SLOTS" }>;

/**
 * SLOTS: renders open slots, or — after a full day / expiry / concurrent-loss — live alternatives, all
 * EXACTLY as the engine returns them (AV-6/AV-8; the widget computes no slot math). Picking a slot
 * places the pre-submit hold and advances to DETAILS; if the slot was claimed in between, the list
 * swaps to fresh alternatives with the released notice — never a dead end (R7/R8). For a group venue a
 * full day surfaces explicit overflow cards to sister venues (R6); the diner must tap to switch.
 */
export function Slots({
  state,
  dispatch,
  config,
  booking,
}: {
  state: SlotsState;
  dispatch: Dispatch<FlowAction>;
  config: VenueConfig;
  booking: BookingConfig;
}) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const { venueId, serviceDay, partySize } = state.search;
  const timeZone = config.timeZone;
  const [placing, setPlacing] = useState<number | null>(null);
  const [holdError, setHoldError] = useState<string | null>(null);

  const slotsQuery = useOpenSlots(venueId, serviceDay, partySize, !state.alternativesOnly);
  const rejectionCode = state.alternativesOnly ? null : (slotsQuery.data?.rejection ?? null);

  // A read-time online-off / not-live rejection routes to the contact fallback (once).
  useEffect(() => {
    if (rejectionCode && classifyRejection(rejectionCode).kind === "contact") {
      dispatch({ type: "CONTACT_FALLBACK", rejectionCode });
    }
  }, [rejectionCode, dispatch]);

  const primarySlots = slotsQuery.data?.slots ?? [];
  const dayFull =
    !state.alternativesOnly && slotsQuery.isSuccess && primarySlots.length === 0 && !rejectionCode;
  const wantAlternatives = state.alternativesOnly || dayFull;

  const altQuery = useQuery({
    queryKey: ["alternatives", venueId, serviceDay, partySize, state.alternativesOnly],
    queryFn: () => fetchAlternatives(venueId, serviceDay!, partySize),
    enabled: wantAlternatives && serviceDay !== null,
    retry: 1,
    staleTime: 0,
  });

  const groupQuery = useGroupAvailability(
    venueId,
    serviceDay,
    partySize,
    booking.isGroup && wantAlternatives,
  );

  async function pick(slot: SelectedSlot) {
    setHoldError(null);
    setPlacing(slot.startSec);
    try {
      const hold = await placeHold(venueId, {
        serviceId: slot.serviceId,
        startSec: slot.startSec,
        partySize,
      });
      dispatch({
        type: "SELECT_SLOT",
        slot,
        hold: { holdId: hold.holdId, expiresAtSec: hold.expiresAtSec },
        idempotencyKey: crypto.randomUUID(),
      });
    } catch (err) {
      const rej = bookingRejectionOf(err, serviceDay ?? "");
      const kind = rej ? classifyRejection(rej.rejectionCode).kind : null;
      if (kind === "alternatives") {
        dispatch({ type: "SLOT_TAKEN" });
        void altQuery.refetch();
      } else if (kind === "contact" && rej) {
        dispatch({ type: "CONTACT_FALLBACK", rejectionCode: rej.rejectionCode });
      } else {
        setHoldError(t("widget.booking.err.holdFailed"));
      }
    } finally {
      setPlacing(null);
    }
  }

  const alternatives: DisplaySlot[] = (altQuery.data ?? []).map((a) => ({
    serviceId: a.serviceId,
    startSec: a.startSec,
    durationSec: a.durationSec,
  }));
  const siblings = (groupQuery.data?.venues ?? []).filter(
    (v) => v.venueId !== venueId && v.slots.length > 0,
  );

  const heading = <h2>{t("widget.booking.slots.title")}</h2>;
  const summary =
    serviceDay !== null ? (
      <p className="rsv-summary">
        {formatServiceDate(serviceDay, timeZone, locale)} ·{" "}
        {t("widget.booking.slots.party", { guests: partySize })}
      </p>
    ) : null;
  const back = (
    <button type="button" className="rsv-secondary" onClick={() => dispatch({ type: "BACK" })}>
      {t("widget.booking.back")}
    </button>
  );

  // Non-fatal read rejection (lead-time / horizon / closed) — an inline note, pick another date.
  if (rejectionCode && classifyRejection(rejectionCode).kind === "inline") {
    return (
      <section className="rsv-step" aria-label={t("widget.booking.slots.title")}>
        {heading}
        <output className="rsv-notice">{t(rejectionMessageKey(rejectionCode))}</output>
        {back}
      </section>
    );
  }

  if (!state.alternativesOnly && slotsQuery.isPending) {
    return (
      <section className="rsv-step" aria-label={t("widget.booking.slots.title")}>
        {heading}
        <output className="rsv-hint" aria-live="polite">
          {t("widget.booking.loading")}
        </output>
      </section>
    );
  }

  if (!state.alternativesOnly && slotsQuery.isError) {
    return (
      <section className="rsv-step" aria-label={t("widget.booking.slots.title")}>
        {heading}
        <p className="rsv-notice" role="alert">
          {t("widget.booking.err.loadFailed")}
        </p>
        <div className="rsv-actions">
          <button type="button" className="rsv-button" onClick={() => void slotsQuery.refetch()}>
            {t("widget.retry")}
          </button>
          {back}
        </div>
      </section>
    );
  }

  const noAvailability =
    wantAlternatives &&
    !altQuery.isPending &&
    alternatives.length === 0 &&
    siblings.length === 0 &&
    !groupQuery.isPending;

  return (
    <section className="rsv-step" aria-label={t("widget.booking.slots.title")}>
      {heading}
      {summary}

      {state.releasedNotice ? (
        <output className="rsv-notice">{t("widget.booking.slots.released")}</output>
      ) : null}
      {holdError ? (
        <p className="rsv-notice" role="alert">
          {holdError}
        </p>
      ) : null}

      {!state.alternativesOnly && primarySlots.length > 0 ? (
        <>
          <h3 id="rsv-slots-open">{t("widget.booking.slots.pickHeading")}</h3>
          <SlotList
            slots={primarySlots}
            onPick={pick}
            timeZone={timeZone}
            locale={locale}
            busyStartSec={placing}
            disabled={placing !== null}
            labelledBy="rsv-slots-open"
          />
        </>
      ) : null}

      {wantAlternatives ? (
        <>
          {altQuery.isPending ? (
            <output className="rsv-hint" aria-live="polite">
              {t("widget.booking.loading")}
            </output>
          ) : alternatives.length > 0 ? (
            <>
              <h3 id="rsv-slots-alt">{t("widget.booking.slots.altHeading")}</h3>
              <SlotList
                slots={alternatives}
                onPick={pick}
                timeZone={timeZone}
                locale={locale}
                busyStartSec={placing}
                disabled={placing !== null}
                labelledBy="rsv-slots-alt"
              />
            </>
          ) : null}

          {siblings.length > 0 ? (
            <>
              <h3>{t("widget.booking.overflow.heading")}</h3>
              {siblings.map((venue) => (
                <OverflowCard
                  key={venue.venueId}
                  venue={venue}
                  onSwitch={(id) => dispatch({ type: "SWITCH_VENUE", venueId: id })}
                />
              ))}
            </>
          ) : null}

          {noAvailability ? (
            <output className="rsv-notice">{t("widget.booking.slots.none")}</output>
          ) : null}
        </>
      ) : null}

      {back}
    </section>
  );
}
